import { SafeEventEmitter, toBytes } from '@/common'
import { SdkError, WebhookSignatureError, WebhookValidationError } from '@/core/errors'
import { Logger } from '@/core/logger'

import { WebhookType } from './webhook.schema'
import { validateWebhookPayload, verifyWebhookSignature } from './webhook.utils'

const textDecoder = new TextDecoder()

/** Raw request body accepted for signature verification (no Node `Buffer` dependency). */
type RawBody = string | Uint8Array | ArrayBuffer

function isRawBody(value: unknown): value is RawBody {
  return typeof value === 'string' || value instanceof Uint8Array || value instanceof ArrayBuffer
}

/**
 * WebhookActionMap
 * - Map of action name => payload. Using Record<string, WebhookPayload> keeps
 *   the typings flexible for providers that supply arbitrary action strings.
 */
export type WebhookActionMap = {
  [A in WebhookType['action']]: Extract<WebhookType, { action: A }>
}

/**
 * WebhookEventMap
 * - Includes specific action keys (via WebhookActionMap), wildcard '*' and 'batch'.
 */
export type WebhookEventMap = WebhookActionMap & {
  '*': WebhookType // Wildcard for any single webhook
  batch: WebhookType[] // Batch of webhooks
}

export interface WebhookManagerOptions {
  /**
   * Secret used for webhook signature verification.
   *
   * If provided, all incoming webhooks must include
   * a valid signature.
   */
  secret?: string

  /**
   * Logger instance used for internal webhook logging.
   */
  logger: Logger
}

/**
 * Handles incoming webhook events from Marzban.
 *
 * Features:
 * - Signature verification
 * - Payload validation
 * - Typed event subscriptions
 * - Wildcard event listeners
 * - Batch webhook processing
 * - Manual event dispatching
 *
 * Supports subscribing to:
 * - Specific webhook actions
 * - All events via '*'
 * - Batch events via 'batch'
 *
 * @example
 * webhook.on('user_created', payload => {
 *   console.log(payload.username)
 * })
 *
 * @example
 * webhook.on('*', payload => {
 *   console.log(payload.action)
 * })
 */
export class WebhookManager {
  private readonly _secret?: string
  private readonly _emitter: SafeEventEmitter<WebhookEventMap>
  private readonly _logger: Logger

  /**
   * @param options Webhook manager options.
   * @param options.secret Optional webhook secret for verifying signatures.
   * @param options.logger Logger instance for logging events.
   */
  constructor({ secret, logger }: WebhookManagerOptions) {
    this._secret = secret
    this._logger = logger
    this._emitter = new SafeEventEmitter<WebhookEventMap>()

    this._logger.debug('WebhookManager initialized', 'WebhookManager')
  }

  // --- Subscription methods ---

  /**
   * Subscribe to an event
   * @param event Event name (specific action, '*', or 'batch')
   * @param listener Function to handle the payload
   */
  on<E extends keyof WebhookEventMap>(event: E, listener: (payload: WebhookEventMap[E]) => void): this {
    this._emitter.on(event, listener)
    this._logger.debug(`Listener added for event: ${String(event)}`, 'WebhookManager')
    return this
  }

  /**
   * Subscribe to an event once (one-time listener)
   */
  once<E extends keyof WebhookEventMap>(event: E, listener: (payload: WebhookEventMap[E]) => void): this {
    this._emitter.once(event, listener)
    this._logger.debug(`One-time listener added for event: ${String(event)}`, 'WebhookManager')
    return this
  }

  /**
   * Unsubscribe a listener from an event
   */
  off<E extends keyof WebhookEventMap>(event: E, listener: (payload: WebhookEventMap[E]) => void): this {
    this._emitter.off(event, listener)
    this._logger.debug(`Listener removed for event: ${String(event)}`, 'WebhookManager')
    return this
  }

  /**
   * Validate incoming webhook payloads and verify signature when secret configured.
   *
   * Important:
   * - If a secret is configured for the manager, the caller MUST pass the raw request body
   *   (string, Uint8Array, or ArrayBuffer) so signature verification is performed against the original bytes.
   *
   * @param rawBody Incoming webhook raw body (string|Uint8Array|ArrayBuffer) OR already-parsed object.
   * @param signature Optional webhook signature to verify (hex string)
   * @returns Array of validated webhook payloads
   */
  async parseWebhook(rawBody: unknown, signature?: string): Promise<WebhookType[]> {
    this._logger.debug('Parsing incoming webhook payload', 'WebhookManager')

    // If secret is configured — require signature and raw body bytes.
    // verifyWebhookSignature itself blocks browser runtimes (server-side only).
    if (this._secret) {
      if (!signature) {
        this._logger.error('Webhook signature is missing but secret is configured', undefined, 'WebhookManager')
        throw new WebhookSignatureError()
      }

      if (!isRawBody(rawBody)) {
        this._logger.error(
          'Webhook secret is configured: parseWebhook requires the raw request body (string, Uint8Array, or ArrayBuffer) for signature verification',
          undefined,
          'WebhookManager'
        )
        throw new WebhookSignatureError('Raw body required for signature verification')
      }

      const ok = await verifyWebhookSignature(signature, this._secret, toBytes(rawBody))
      if (!ok) {
        // Do not log rawBody — it may contain user PII. The signature is a hash, not a secret.
        this._logger.error('Webhook signature verification failed', { signature }, 'WebhookManager')
        throw new WebhookSignatureError()
      } else {
        this._logger.debug('Webhook signature verified successfully', 'WebhookManager')
      }
    }

    // Parse JSON if necessary
    let parsed: unknown = rawBody
    if (isRawBody(rawBody)) {
      try {
        parsed = JSON.parse(typeof rawBody === 'string' ? rawBody : textDecoder.decode(rawBody))
      } catch (err) {
        this._logger.error('Failed to parse webhook JSON body', err, 'WebhookManager')
        // Let validateWebhookPayload throw a structured error if necessary,
        // but provide a clear message for parse errors.
        throw new WebhookValidationError(err)
      }
    }

    // Validate payload structure (will throw WebhookValidationError on failure)
    const payloads = validateWebhookPayload(parsed)
    this._logger.debug(`Validated ${payloads.length} webhook payload(s)`, 'WebhookManager')

    return payloads
  }

  // --- Basic processing ---

  /**
   * Handle an incoming webhook:
   * - Verifies signature (if secret configured)
   * - Validates the payload
   * - Emits the 'batch' event once with all payloads
   * - Emits an individual event for each payload's action
   * - Emits the wildcard '*' event for each payload
   *
   * The returned promise resolves only after all listeners (including async
   * ones) have settled, so awaiting it guarantees processing is complete
   * before you respond to the request.
   *
   * @param rawBody Incoming webhook raw body (string|Uint8Array|ArrayBuffer) or already-parsed object
   * @param signature Optional signature for verification
   * @returns true if at least one event had listeners
   */
  async handleWebhook(rawBody: unknown, signature?: string): Promise<boolean> {
    this._logger.debug('Handling incoming webhook', 'WebhookManager')
    let emitted = false

    try {
      const payloads = await this.parseWebhook(rawBody, signature)

      // Emit batch event once with all payloads, awaiting listeners.
      if (await this._emitter.emitAsync('batch', payloads)) {
        this._logger.debug('Batch event emitted', 'WebhookManager')
        emitted = true
      }

      // Emit each individual action and '*' wildcard events
      for (const payload of payloads) {
        if (await this._emitter.emitAsync(payload.action, payload)) {
          this._logger.debug(`Event emitted for action: ${payload.action}`, 'WebhookManager')
          emitted = true
        }
        if (await this._emitter.emitAsync('*', payload)) {
          this._logger.debug(`Wildcard '*' event emitted for action: ${payload.action}`, 'WebhookManager')
          emitted = true
        }
      }

      this._logger.info(`Webhook processing completed, emitted=${emitted}`, 'WebhookManager')
    } catch (err) {
      this._logger.error('Error handling webhook', err, 'WebhookManager')
      if (err instanceof SdkError) throw err
      throw new WebhookValidationError(err)
    }

    return emitted
  }

  /**
   * Manually dispatch an event
   * @param event Event name (specific action, '*', or 'batch')
   * @param payload Payload to emit
   * @returns true if event had listeners
   */
  async dispatch<E extends keyof Omit<WebhookEventMap, '*' | 'batch'>>(
    event: E,
    payload: WebhookEventMap[E]
  ): Promise<boolean> {
    this._logger.debug(`Dispatching event: ${String(event)}`, 'WebhookManager')
    const result = await this._emitter.emitAsync(event, payload)
    this._logger.debug(`Event dispatched: ${String(event)}, result=${result}`, 'WebhookManager')
    return result
  }
}
